import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Maximize, Video, Volume2, VolumeX, WifiOff } from "lucide-react";

// Plays a HazCam station natively (HLS) inside Storm Desk — no external site needed.
export const SkycamPlayer = ({ cam, compact = false, testId, muted: initialMuted = true }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(initialMuted);
  const [posterTick, setPosterTick] = useState(0);
  const src = cam?.video_url;

  useEffect(() => {
    const video = videoRef.current;
    setReady(false);
    setFailed(false);
    if (!video || !src) return undefined;
    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, liveSyncDurationCount: 3, maxBufferLength: 20 });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { setReady(true); video.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && data.details !== Hls.ErrorDetails.MANIFEST_LOAD_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else setFailed(true);
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", () => setReady(true), { once: true });
      video.addEventListener("error", () => setFailed(true), { once: true });
    } else {
      setFailed(true);
    }
    return () => { if (hls) hls.destroy(); };
  }, [src]);

  useEffect(() => {
    if (!failed) return undefined;
    const timer = setInterval(() => setPosterTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, [failed]);

  if (!cam) return null;
  const toggleMute = () => { const video = videoRef.current; if (!video) return; video.muted = !video.muted; setMuted(video.muted); };
  const fullscreen = () => { const element = wrapRef.current; if (!element) return; if (document.fullscreenElement) document.exitFullscreen(); else element.requestFullscreen?.(); };

  return (
    <div ref={wrapRef} data-testid={testId || `skycam-player-${cam.id || cam.station || "frame"}`} className="group relative h-full w-full overflow-hidden bg-[#02040c]">
      <video ref={videoRef} data-testid={`${testId || "skycam-player"}-video`} poster={cam.poster} muted={muted} playsInline autoPlay className={`block h-full w-full object-cover ${failed ? "hidden" : ""}`} />
      {failed && (
        <img src={`${cam.poster}?t=${posterTick}`} alt={cam.name} data-testid={`${testId || "skycam-player"}-snapshot`} className="block h-full w-full object-cover" />
      )}
      <div className="pointer-events-none absolute left-2 top-2 z-10 flex items-center gap-1.5">
        <span className={`mono inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white ${failed ? "bg-slate-700" : "bg-red-600"}`}>
          {failed ? <WifiOff className="h-3 w-3" /> : <span className="live-dot h-1.5 w-1.5 rounded-full bg-white" />}
          {failed ? "Snapshot" : ready ? "Live" : "Connecting"}
        </span>
      </div>
      {!compact && (
        <div className="pointer-events-none absolute bottom-2 left-2 z-10 flex items-center gap-2 border border-cyan-300/25 bg-[rgba(2,6,16,0.82)] px-2.5 py-1.5 backdrop-blur-md">
          <Video className="h-3 w-3 text-cyan-300" />
          <span className="mono text-[10px] font-bold uppercase tracking-[0.18em] text-white">{cam.name}</span>
          {cam.location && <span className="mono border-l border-white/15 pl-2 text-[10px] text-cyan-100">{cam.location}</span>}
        </div>
      )}
      {!compact && !failed && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button type="button" data-testid={`${testId || "skycam-player"}-mute-button`} onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} className="grid h-8 w-8 place-items-center border border-white/15 bg-black/70 text-slate-100 hover:bg-black">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button type="button" data-testid={`${testId || "skycam-player"}-fullscreen-button`} onClick={fullscreen} aria-label="Fullscreen" className="grid h-8 w-8 place-items-center border border-white/15 bg-black/70 text-slate-100 hover:bg-black">
            <Maximize className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SkycamPlayer;
